import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileText, CheckCircle, AlertCircle, ArrowRight, X } from 'lucide-react';

const MigrationWizard = ({ onClose }) => {
  const { token } = useAuth();
  const API = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  const handleParse = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(API+'/api/migration/parse-csv', {
        method:'POST', headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
        body: JSON.stringify({ csv: csvText })
      });
      const data = await res.json();
      if (data.partners) { setParsed(data.partners); setStep(2); }
      else setError(data.error || 'Parse failed');
    } catch(e) { setError('Parse failed: '+e.message); }
    setLoading(false);
  };

  const handleImport = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(API+'/api/migration/import-partners', {
        method:'POST', headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
        body: JSON.stringify({ partners: parsed })
      });
      const data = await res.json();
      setResults(data); setStep(3);
    } catch(e) { setError('Import failed: '+e.message); }
    setLoading(false);
  };

  const boxStyle = { background:'#1e293b', borderRadius:'16px', maxWidth:'650px', width:'100%', border:'1px solid #334155', overflow:'hidden' };
  const btnGreen = { background:'#10b981', color:'white', border:'none', padding:'12px 24px', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'20px' }}>
      <div style={boxStyle}>
        <div style={{ background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', padding:'24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ color:'white', fontSize:'22px', fontWeight:'800', margin:0 }}>Migration Wizard</h2>
            <p style={{ color:'rgba(255,255,255,0.8)', fontSize:'13px', margin:0 }}>Import partners from SPS Commerce or any EDI provider</p>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'white', borderRadius:'50%', width:'32px', height:'32px', cursor:'pointer' }}><X size={16}/></button>
        </div>
        <div style={{ padding:'24px' }}>
          {error && <div style={{ background:'#fee2e2', color:'#991b1b', padding:'12px', borderRadius:'8px', marginBottom:'16px', fontSize:'13px' }}>{error}</div>}

          {step === 1 && (
            <div>
              <p style={{ color:'#94a3b8', fontSize:'14px', marginBottom:'16px' }}>Upload a CSV file with your trading partners. The CSV should have columns like: Name, EDI ID, SFTP Host, Username, Password.</p>
              <div style={{ border:'2px dashed #334155', borderRadius:'12px', padding:'32px', textAlign:'center', marginBottom:'16px' }}>
                <Upload size={32} style={{ color:'#64748b', marginBottom:'8px' }}/>
                <p style={{ color:'#94a3b8', fontSize:'14px', marginBottom:'12px' }}>Drop your CSV here or click to browse</p>
                <input type="file" accept=".csv,.txt" onChange={handleFileUpload} style={{ marginBottom:'8px' }}/>
              </div>
              <p style={{ color:'#64748b', fontSize:'12px', marginBottom:'8px' }}>Or paste CSV data directly:</p>
              <textarea value={csvText} onChange={e=>setCsvText(e.target.value)} placeholder="Name,EDI ID,SFTP Host,Username,Password&#10;Walmart,0078742000000,sftp.walmart.com,myuser,mypass" style={{ width:'100%', height:'100px', background:'#0f172a', color:'#e2e8f0', border:'1px solid #334155', borderRadius:'8px', padding:'12px', fontFamily:'monospace', fontSize:'12px', resize:'vertical', boxSizing:'border-box' }}/>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'16px' }}>
                <button onClick={handleParse} disabled={!csvText||loading} style={{...btnGreen, opacity:!csvText?0.5:1}}>
                  {loading?'Parsing...':'Parse CSV'} <ArrowRight size={16}/>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p style={{ color:'#10b981', fontSize:'14px', marginBottom:'16px' }}><CheckCircle size={16} style={{verticalAlign:'middle'}}/> Found {parsed.length} partners. Review and import:</p>
              <div style={{ maxHeight:'250px', overflowY:'auto', marginBottom:'16px' }}>
                {parsed.map((p,i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'10px 12px', background: i%2?'#0f172a':'transparent', borderRadius:'6px' }}>
                    <span style={{ color:'white', fontWeight:'600', fontSize:'14px' }}>{p.name||'Unnamed'}</span>
                    <span style={{ color:'#64748b', fontFamily:'monospace', fontSize:'12px' }}>{p.ediId||'No EDI ID'}</span>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <button onClick={()=>setStep(1)} style={{ background:'none', border:'1px solid #334155', color:'#94a3b8', padding:'12px 24px', borderRadius:'10px', cursor:'pointer', fontSize:'14px' }}>Back</button>
                <button onClick={handleImport} disabled={loading} style={btnGreen}>{loading?'Importing...':'Import All Partners'} <ArrowRight size={16}/></button>
              </div>
            </div>
          )}

          {step === 3 && results && (
            <div style={{ textAlign:'center' }}>
              <CheckCircle size={48} style={{ color:'#10b981', marginBottom:'16px' }}/>
              <h3 style={{ color:'white', fontSize:'20px', marginBottom:'8px' }}>Migration Complete</h3>
              <p style={{ color:'#94a3b8', fontSize:'14px', marginBottom:'24px' }}>Imported {results.imported} of {results.total} partners successfully.</p>
              {results.results?.map((r,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', fontSize:'13px' }}>
                  <span style={{ color:'white' }}>{r.name}</span>
                  <span style={{ color: r.status==='created'?'#10b981':'#ef4444' }}>{r.status==='created'?'✅ Created':'❌ '+r.error}</span>
                </div>
              ))}
              <button onClick={onClose} style={{...btnGreen, margin:'24px auto 0'}}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default MigrationWizard;
